import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Badge,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  List,
  Avatar,
  Spin,
  Typography,
  Select,
  DatePicker,
  Slider,
  Divider
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  BuildOutlined,
  TeamOutlined,
  HomeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoginOutlined,
  FilterOutlined,
  ClearOutlined
} from "@ant-design/icons";
import companyService from "../../../services/company/company.service";
import { Company, CompanyStatus } from "../../../types/company.types";
import type { ColumnsType } from "antd/es/table";
import "./styles.css";
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FooterSection from "../../shared/components/footerSection";
import superAdminService from "../../../services/super-admin/super-admin.service";
import authService from "../../../services/auth/auth.service";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { checkAuth } from "../../../store/slices/auth.slice";
import { PermissionType } from "../../../types/user.types";
import moment from "moment";
import type { Dayjs } from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';

const { confirm } = Modal;
const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AllCompanies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  
  // Filtreleme state'leri
  const [filters, setFilters] = useState({
    status: undefined as CompanyStatus | undefined,
    employeeCountRange: [0, 1000] as [number, number],
    subscriptionDateRange: null as RangePickerProps['value'],
    hasActiveSubscription: undefined as boolean | undefined,
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // impersonation modal state
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [admins, setAdmins] = useState<Array<{ id: number; firstName: string; lastName: string; email: string }>>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [companies, searchText, filters]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await companyService.getAllCompanies();
      setCompanies(data);
      setFilteredCompanies(data);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = companies.filter((company) => {
      // Metin arama
      const matchesSearch = 
        company.name.toLowerCase().includes(searchText.toLowerCase()) ||
        company.taxNumber.toLowerCase().includes(searchText.toLowerCase());
      
      if (!matchesSearch) return false;

      // Durum filtresi
      if (filters.status && company.status !== filters.status) {
        return false;
      }

      // Çalışan sayısı filtresi
      const employeeCount = company.users?.length || 0;
      if (employeeCount < filters.employeeCountRange[0] || employeeCount > filters.employeeCountRange[1]) {
        return false;
      }

      // Abonelik tarihi filtresi
      if (filters.subscriptionDateRange && company.activeSubscriptionExpiresAt) {
        const subscriptionDate = moment(company.activeSubscriptionExpiresAt);
        const [startDate, endDate] = filters.subscriptionDateRange;
        if (startDate && endDate) {
          const startMoment = moment(startDate.toDate());
          const endMoment = moment(endDate.toDate());
          if (!subscriptionDate.isBetween(startMoment, endMoment, 'day', '[]')) {
            return false;
          }
        }
      }

      // Aktif abonelik filtresi
      if (filters.hasActiveSubscription !== undefined) {
        const hasActive = company.activeSubscriptionExpiresAt && 
          moment(company.activeSubscriptionExpiresAt).isAfter(moment());
        if (filters.hasActiveSubscription !== hasActive) {
          return false;
        }
      }

      return true;
    });

    setFilteredCompanies(filtered);
  };

  const clearFilters = () => {
    setFilters({
      status: undefined,
      employeeCountRange: [0, 1000],
      subscriptionDateRange: null,
      hasActiveSubscription: undefined,
    });
    setSearchText("");
  };

  const getMaxEmployeeCount = () => {
    return Math.max(...companies.map(c => c.users?.length || 0), 100);
  };

  const handleDelete = (id: number) => {
    confirm({
      title: "Bu şirketi silmek istediğinize emin misiniz?",
      icon: <ExclamationCircleOutlined />,
      content: "Bu işlem geri alınamaz!",
      okText: "Evet",
      okType: "danger",
      cancelText: "Hayır",
      onOk: async () => {
        try {
          await companyService.deleteCompany(id);
          message.success("Şirket başarıyla silindi.");
          fetchCompanies();
        } catch (error: any) {
          handleApiError(error, t);
        }
      },
    });
  };

  const getStatusBadge = (status: CompanyStatus) => {
    switch (status) {
      case CompanyStatus.ACTIVE:
        return <Badge status="success" text="Aktif" />;
      case CompanyStatus.INACTIVE:
        return <Badge status="default" text="Pasif" />;
      case CompanyStatus.DELETED:
        return <Badge status="error" text="Silindi" />;
      default:
        return <Badge status="processing" text="Bilinmiyor" />;
    }
  };

  const openImpersonateModal = async (companyId: number) => {
    setSelectedCompanyId(companyId);
    setImpersonateModalOpen(true);
    setAdmins([]);
    setAdminsLoading(true);
    try {
      const list = await superAdminService.getCompanyAdmins(companyId);
      setAdmins(list || []);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleImpersonate = async (userId: number) => {
    if (!userId) return;
    setImpersonateLoading(true);
    try {
      // Orijinal süper admin token & user'ı sakla (geri dönüş için)
      const originalToken = localStorage.getItem('token') || '';
      const originalUser = localStorage.getItem('user') || '';
      localStorage.setItem('originalToken', originalToken);
      localStorage.setItem('originalUser', originalUser);
      localStorage.setItem('originalReturnUrl', window.location.pathname + window.location.search);

      const data = await superAdminService.impersonate(userId);
      // Orijinal token'ı saklama kaldırıldı
      localStorage.setItem('token', data.token);
      authService.setUser(data.user);
      
      // CompanyId'yi hem user.companyId hem de user.company.id'den al
      const effectiveCompanyId = data.user?.companyId || (data.user as any)?.company?.id || selectedCompanyId;
      
      if (effectiveCompanyId != null) {
        localStorage.setItem('companyId', String(effectiveCompanyId));
      }
      // Redux auth state'ini anında güncelle
      dispatch(checkAuth());
      message.success('Admin olarak giriş yapıldı');
      navigate(all_routes.adminDashboard);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setImpersonateLoading(false);
      setImpersonateModalOpen(false);
    }
  };

  const columns: ColumnsType<Company> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "Şirket Adı",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
      render: (text, record) => (
        <Link
          to={`${all_routes.superAdminCompanyDetails.replace(':id', record.id.toString())}`}
          className="company-name-link"
          title={text}
        >
          {text.length > 30 ? `${text.substring(0, 30)}...` : text}
        </Link>
      ),
    },
    {
      title: "Vergi Numarası",
      dataIndex: "taxNumber",
      key: "taxNumber",
      sorter: (a, b) => a.taxNumber.localeCompare(b.taxNumber),
      sortDirections: ['ascend', 'descend'],
    },
    // {
    //   title: "E-posta",
    //   dataIndex: "email",
    //   key: "email",
    //   responsive: ["md"],
    // },
    {
      title: "Abonelik Bitiş Tarihi",
      dataIndex: "activeSubscriptionExpiresAt",
      key: "activeSubscriptionExpiresAt",
      sorter: (a, b) => {
        if (!a.activeSubscriptionExpiresAt && !b.activeSubscriptionExpiresAt) return 0;
        if (!a.activeSubscriptionExpiresAt) return 1;
        if (!b.activeSubscriptionExpiresAt) return -1;
        return moment(a.activeSubscriptionExpiresAt).valueOf() - moment(b.activeSubscriptionExpiresAt).valueOf();
      },
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? moment(date).format('DD.MM.YYYY') : '-',
      responsive: ["lg"],
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const statusOrder = { [CompanyStatus.ACTIVE]: 1, [CompanyStatus.INACTIVE]: 2, [CompanyStatus.DELETED]: 3 };
        return statusOrder[a.status] - statusOrder[b.status];
      },
      sortDirections: ['ascend', 'descend'],
      render: (status) => getStatusBadge(status),
    },
    {
      title: "Çalışan Sayısı",
      key: "employeeCount",
      sorter: (a, b) => (a.users?.length || 0) - (b.users?.length || 0),
      sortDirections: ['ascend', 'descend'],
      responsive: ["lg"],
      render: (_, record) => (
        <Tag color="blue">
          <TeamOutlined /> {record.users?.length || 0}
        </Tag>
      ),
    },
    // {
    //   title: "Şube Sayısı",
    //   key: "branchCount",
    //   responsive: ["lg"],
    //   render: (_, record) => (
    //     <Tag color="green">
    //       <HomeOutlined /> {record.branches?.length || 0}
    //     </Tag>
    //   ),
    // },
    {
      title: "İşlemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          {/* Şirket Detayları - VIEW_COMPANIES permission */}
          {user?.permissions?.includes(PermissionType.SUPER_ADMIN_VIEW_COMPANIES) && (
            <Tooltip title="Şirket Detayları">
              <Link to={`${all_routes.superAdminCompanyDetails.replace(':id', record.id.toString())}`}>
                <Button type="primary" size="small" icon={<BuildOutlined />} />
              </Link>
            </Tooltip>
          )}
          
          {/* Impersonation - IMPERSONATE permission */}
          {user?.permissions?.includes(PermissionType.SUPER_ADMIN_IMPERSONATE) && (
            <Tooltip title="Admin olarak gir">
              <Button size="small" icon={<LoginOutlined />} onClick={() => openImpersonateModal(record.id)} />
            </Tooltip>
          )}
          
          {/* Şirket Silme - DELETE_COMPANY permission */}
          {user?.permissions?.includes(PermissionType.SUPER_ADMIN_DELETE_COMPANY) && (
            <Tooltip title="Şirketi Sil">
              <Button
                type="default"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Şirketler</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard}>
                      <i className="ti ti-smart-home"></i>
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Şirketler
                  </li>
                </ol>
              </nav>
            </div>
            <div className=" ms-2">
              {/* Yeni Şirket Butonu - CREATE_COMPANY permission */}
              {user?.permissions?.includes(PermissionType.SUPER_ADMIN_CREATE_COMPANY) && (
                <Link
                  to={`${all_routes.superAdminCompanyCreate}`}
                  className="btn btn-primary"
                >
                  <PlusOutlined /> Yeni Şirket
                </Link>
              )}
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* İstatistikler */}
          <Row gutter={16} className="mb-4">
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Toplam Şirket"
                  value={companies.length}
                  prefix={<BuildOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Aktif Şirketler"
                  value={
                    companies.filter(
                      (company) => company.status === CompanyStatus.ACTIVE
                    ).length
                  }
                  prefix={<Badge status="success" />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Pasif Şirketler"
                  value={
                    companies.filter(
                      (company) =>
                        company.status === CompanyStatus.INACTIVE ||
                        company.status === CompanyStatus.DELETED
                    ).length
                  }
                  prefix={<Badge status="default" />}
                />
              </Card>
            </Col>
          </Row>

          {/* Arama ve Filtreleme */}
          <Card className="company-list-card mb-0">
            <div className="mb-3">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={16} md={18}>
                  <Input
                    placeholder="Şirket adı veya vergi numarası ile ara"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="company-search"
                    size="large"
                  />
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Space>
                    <Button
                      type={showFilters ? "primary" : "default"}
                      icon={<FilterOutlined />}
                      onClick={() => setShowFilters(!showFilters)}
                      size="large"
                    >
                      Filtreler
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={clearFilters}
                      size="large"
                      title="Filtreleri Temizle"
                    >
                      Temizle
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>

            {/* Filtreleme Paneli */}
            {showFilters && (
              <Card size="small" className="mb-3" style={{ backgroundColor: '#fafafa' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ fontSize: '12px', color: '#666' }}>Durum</Text>
                      <Select
                        placeholder="Durum seçin"
                        style={{ width: '100%', marginTop: 4 }}
                        allowClear
                        value={filters.status}
                        onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                      >
                        <Option value={CompanyStatus.ACTIVE}>
                          <Badge status="success" text="Aktif" />
                        </Option>
                        <Option value={CompanyStatus.INACTIVE}>
                          <Badge status="default" text="Pasif" />
                        </Option>
                        <Option value={CompanyStatus.DELETED}>
                          <Badge status="error" text="Silindi" />
                        </Option>
                      </Select>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ fontSize: '12px', color: '#666' }}>Abonelik Durumu</Text>
                      <Select
                        placeholder="Abonelik durumu"
                        style={{ width: '100%', marginTop: 4 }}
                        allowClear
                        value={filters.hasActiveSubscription}
                        onChange={(value) => setFilters(prev => ({ ...prev, hasActiveSubscription: value }))}
                      >
                        <Option value={true}>
                          <Badge status="success" text="Aktif Abonelik" />
                        </Option>
                        <Option value={false}>
                          <Badge status="error" text="Süresi Dolmuş" />
                        </Option>
                      </Select>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ fontSize: '12px', color: '#666' }}>
                        Çalışan Sayısı: {filters.employeeCountRange[0]} - {filters.employeeCountRange[1]}
                      </Text>
                      <Slider
                        range
                        min={0}
                        max={getMaxEmployeeCount()}
                        value={filters.employeeCountRange}
                        onChange={(value) => setFilters(prev => ({ ...prev, employeeCountRange: value as [number, number] }))}
                        style={{ marginTop: 8 }}
                        tooltip={{ formatter: (value) => `${value} çalışan` }}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong style={{ fontSize: '12px', color: '#666' }}>Abonelik Bitiş Tarihi</Text>
                      <RangePicker
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder={['Başlangıç', 'Bitiş']}
                        format="DD.MM.YYYY"
                        value={filters.subscriptionDateRange}
                        onChange={(dates) => setFilters(prev => ({ ...prev, subscriptionDateRange: dates }))}
                      />
                    </div>
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0 8px 0' }} />
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {filteredCompanies.length} şirket listeleniyor ({companies.length} toplam)
                  </Text>
                </div>
              </Card>
            )}

            <Table
              columns={columns}
              dataSource={filteredCompanies}
              rowKey="id"
              loading={loading}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: ["10", "20", "50", "100"],
                // showSizeChanger: true,
                // showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} arası, toplam ${total} şirket`,
              }}
              scroll={{ x: 1200 }}
              size="middle"
              bordered={false}
              showSorterTooltip={{
                title: 'Sıralamak için tıklayın'
              }}
            />
          </Card>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>

      <Modal
        title={`Admin olarak gir — Şirket #${selectedCompanyId ?? ''}`}
        open={impersonateModalOpen}
        onCancel={() => setImpersonateModalOpen(false)}
        footer={null}
      >
        {adminsLoading ? (
          <div className="d-flex justify-content-center py-4"><Spin /></div>
        ) : admins.length === 0 ? (
          <Text type="secondary">Bu şirkete ait aktif admin bulunamadı.</Text>
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={admins}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key="enter"
                    type="primary"
                    size="small"
                    loading={impersonateLoading}
                    onClick={() => handleImpersonate(item.id)}
                  >
                    Gir
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar>{(item.firstName?.[0] || '').toUpperCase()}</Avatar>}
                  title={`${item.firstName} ${item.lastName}`}
                  description={item.email}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
};

export default AllCompanies;
